import { type ReactElement } from 'react';
import {
  Card,
  Text,
  Space,
  Button,
  Group,
  Modal,
  ThemeIcon,
  ScrollArea,
} from '@mantine/core';
import { FaInfo } from 'react-icons/fa';
import { HiOutlinePlus } from 'react-icons/hi';
import { TbTrashXFilled } from 'react-icons/tb';
import { useDisclosure } from '@mantine/hooks';
import { EmployeeModal } from './EmployeeModal';
import { type UnwantedEmployees } from '@/schemas/API/GET/client/preferences/response';
import { deleteFetcher, getFetcher, postFetcher } from '@/api/fetchers';
import useSWR from 'swr';
import { useParams } from 'react-router-dom';
import React from 'react';

export const ClientEmployeePreferencesSection = (): ReactElement => {
  const [modalOpened, { open, close }] = useDisclosure(false);

  const { clientId } = useParams<{ clientId: string }>();
  const { data: unwantedEmployees, mutate: mutateUnwantedEmployees } =
    useSWR<UnwantedEmployees>(
      clientId ? `/clients/${clientId}/unwanted-employees` : null,
      getFetcher
    );

  const deleteEmployee = async (employeeId: number) => {
    await deleteFetcher(`clients/${clientId}/unwanted-employees/${employeeId}`);
    mutateUnwantedEmployees(
      {
        unwantedList: unwantedEmployees
          ? unwantedEmployees.unwantedList.filter(
              employee => employee.id !== employeeId
            )
          : [],
      },
      false
    );
  };
  const handleNewEmployees = async (newEmployees: number[]) => {
    const updatedEmployees: UnwantedEmployees = await postFetcher(
      `/clients/${clientId}/unwanted-employees`,
      {
        entityIds: newEmployees,
      }
    );
    mutateUnwantedEmployees(updatedEmployees, false);
  };

  const employeeRows = unwantedEmployees?.unwantedList.map(employee => (
    <React.Fragment key={employee.id}>
      <Card shadow="none" withBorder>
        <Group justify="space-between">
          <Group>
            <Text>{employee.firstName}</Text>
            <Text>{employee.lastName}</Text>
          </Group>
          <Button
            data-testid={employee.id}
            variant="subtle"
            onClick={() => {
              deleteEmployee(employee.id);
            }}
          >
            <TbTrashXFilled color="black" size="1.9em" />
          </Button>
        </Group>
      </Card>
      <Space h="lg" />
    </React.Fragment>
  ));

  return (
    <Card shadow="sm" radius="md" withBorder>
      <Group justify="space-between">
        <Text fw={700} size="lg">
          Nežádoucí zaměstnanci
        </Text>
        <Button
          data-testid="add"
          variant="subtle"
          onClick={() => {
            open();
          }}
        >
          <HiOutlinePlus color="black" size="1.9em" />
        </Button>
      </Group>
      <Space h="lg" />
      {unwantedEmployees && unwantedEmployees.unwantedList.length === 0 ? (
        <Card shadow="none" withBorder>
          <Group justify="space-between">
            <Group>
              <ThemeIcon variant="white">
                <FaInfo style={{ width: '70%', height: '70%' }} />
              </ThemeIcon>
              <p>Nejsou vybráni nežádoucí zaměstnanci</p>
            </Group>
          </Group>
        </Card>
      ) : (
        <ScrollArea>{employeeRows}</ScrollArea>
      )}
      <Modal
        opened={modalOpened}
        onClose={close}
        centered
        withCloseButton={false}
        size="lg"
      >
        <EmployeeModal
          closeModal={(newEmployees: number[] | null) => {
            close();
            if (newEmployees !== null) {
              handleNewEmployees(newEmployees);
            }
          }}
          startIds={
            unwantedEmployees
              ? unwantedEmployees.unwantedList.map(employee => employee.id)
              : []
          }
        ></EmployeeModal>
      </Modal>
    </Card>
  );
};
